import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  GoldRateFormModal,
  GoldRateViewModal,
} from 'components/modals/modules/master-module/GoldRateMasterModal';
import type {
  GoldRateFormData,
  GoldRateRecord,
} from 'components/modals/modules/master-module/GoldRateMasterModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useGoldRates, useCreateGoldRate, useUpdateGoldRate, useDeleteGoldRate, useActivateGoldRate } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';

// ============================================================================
// GRID VIEW CARD COMPONENT
// ============================================================================

const GoldRateCard: React.FC<{
  record: GoldRateRecord;
  onView: (record: GoldRateRecord) => void;
  onEdit: (record: GoldRateRecord) => void;
  onDelete: (record: GoldRateRecord) => void;
  onActivate: (record: GoldRateRecord) => void;
}> = ({ record, onView, onEdit, onDelete, onActivate }) => {
  return (
    <div style={{
      background: 'var(--glass-bg)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      border: record.isActive ? '2px solid var(--success)' : '1px solid var(--border-color)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      transition: 'var(--transition)',
      cursor: 'pointer',
      position: 'relative',
    }}
    onClick={() => onView(record)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{record.id}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {formatCurrency(record.ratePerGram)}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>per gram</div>
        </div>
        <span style={getStatusBadgeStyle(record.isActive ? 'ACTIVE' : 'INACTIVE')}>
          {record.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rate Date</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{formatDate(record.rateDate)}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Business Unit</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{record.businessUnitName}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{record.createdByName || record.createdBy}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{formatDate(record.createdAt)}</div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        marginTop: '0.75rem',
      }}>
        <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={(e) => { e.stopPropagation(); onEdit(record); }}>Edit</button>
        {!record.isActive && (
          <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={(e) => { e.stopPropagation(); onActivate(record); }}>Activate</button>
        )}
        <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: 'var(--danger)' }}
          onClick={(e) => { e.stopPropagation(); onDelete(record); }}>Delete</button>
      </div>
    </div>
  );
};

// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: GoldRateFormData = {
  rateDate: new Date().toISOString().split('T')[0],
  ratePerGram: '',
  rateBasis: 'NET',
};

// ============================================================================
// MAIN GOLD RATE MASTER SCREEN COMPONENT
// ============================================================================

const GoldRateMasterScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useGoldRates();
  const { createGoldRate, loading: createLoading } = useCreateGoldRate();
  const { updateGoldRate, loading: updateLoading } = useUpdateGoldRate();
  const { deleteGoldRate, loading: deleteLoading } = useDeleteGoldRate();
  const { activateGoldRate, loading: activateLoading } = useActivateGoldRate();

  // Data state
  const goldRates = (apiData as unknown as GoldRateRecord[]) || [];
  const isMutating = createLoading || updateLoading || deleteLoading || activateLoading;

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GoldRateRecord[]>([]);
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(10);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<GoldRateRecord | null>(null);
  const [formData, setFormData] = useState<GoldRateFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rateMin, setRateMin] = useState('');
  const [rateMax, setRateMax] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    const filtered = goldRates.filter((rate) => {
      const matchesSearch = !searchQuery ||
        rate.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rate.businessUnitName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(rate.ratePerGram).includes(searchQuery) ||
        formatDate(rate.rateDate).includes(searchQuery);

      const matchesStatus = statusFilter === '' ||
        (statusFilter === 'Active' && rate.isActive) ||
        (statusFilter === 'Inactive' && !rate.isActive);

      const matchesBu = buFilter === '' || rate.businessUnitId === buFilter;

      const matchesDateFrom = !dateFrom || rate.rateDate >= dateFrom;
      const matchesDateTo = !dateTo || rate.rateDate <= dateTo;

      const matchesRateMin = !rateMin || rate.ratePerGram >= parseFloat(rateMin);
      const matchesRateMax = !rateMax || rate.ratePerGram <= parseFloat(rateMax);

      const matchesCreatedBy = !createdByFilter || rate.createdBy === createdByFilter;

      return matchesSearch && matchesStatus && matchesBu && matchesDateFrom && matchesDateTo && matchesRateMin && matchesRateMax && matchesCreatedBy;
    });

    // Sort: Active records first, then by date descending within each status
    return filtered.sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return b.rateDate.localeCompare(a.rateDate);
    });
  },
    [goldRates, searchQuery, statusFilter, buFilter, dateFrom, dateTo, rateMin, rateMax, createdByFilter]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    // CR-002: basis defaults to the currently active rate's basis, so daily updates
    // carry the mode forward until the admin deliberately switches at cut-over
    const activeBasis = goldRates.find((r) => r.isActive)?.rateBasis ?? 'NET';
    setFormData({ ...emptyFormData, rateBasis: activeBasis });
    setFormModalOpen(true);
  }, [goldRates]);

  const handleEdit = useCallback((record: GoldRateRecord) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setFormData({
      rateDate: record.rateDate,
      ratePerGram: String(record.ratePerGram),
      rateBasis: record.rateBasis ?? 'NET',
    });
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((record: GoldRateRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    const action = formMode === 'create' ? 'Create' : 'Save';
    const message = formMode === 'create'
      ? 'Are you sure you want to create this gold rate?'
      : 'Are you sure you want to save changes to this gold rate?';

    setConfirmDialog({
      open: true,
      title: `${action} Gold Rate`,
      message,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (formMode === 'create') {
            const created = await createGoldRate({
              rateDate: formData.rateDate,
              ratePerGram: parseFloat(formData.ratePerGram) || 0,
              rateBasis: formData.rateBasis,
              entityId: 'ENT001',
              businessUnitId: 'BU001',
            });
            // Close modal immediately after successful create
            setFormModalOpen(false);
            // Auto-activate the newly created rate (separate try-catch so it doesn't block)
            if (created?.id) {
              try {
                await activateGoldRate(created.id);
              } catch {
                // Activation failed silently - rate was still created
              }
            }
            await refetch();
            showMessage('Gold rate created and activated successfully', 'success');
          } else if (selectedRecord) {
            await updateGoldRate(selectedRecord.id, {
              id: selectedRecord.id,
              rateDate: formData.rateDate,
              ratePerGram: parseFloat(formData.ratePerGram) || 0,
              rateBasis: formData.rateBasis,
            });
            await refetch();
            showMessage('Gold rate updated successfully', 'success');
            setFormModalOpen(false);
          }
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || `Failed to ${formMode === 'create' ? 'create' : 'update'} gold rate`, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, showMessage, createGoldRate, updateGoldRate, activateGoldRate, refetch]);

  const handleDelete = useCallback((record: GoldRateRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Gold Rate',
      message: `Are you sure you want to delete gold rate ${record.id} (${formatDate(record.rateDate)})?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteGoldRate(record.id);
          await refetch();
          showMessage('Gold rate deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete gold rate', 'error');
        }
      },
    });
  }, [showMessage, deleteGoldRate, refetch]);

  const handleActivate = useCallback((record: GoldRateRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Activate Gold Rate',
      message: `Set rate ${record.id} (${formatCurrency(record.ratePerGram)}/gram on ${formatDate(record.rateDate)}) as the active rate? The current active rate will be deactivated.`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await activateGoldRate(record.id);
          await refetch();
          showMessage('Gold rate activated successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to activate gold rate', 'error');
        }
      },
    });
  }, [showMessage, activateGoldRate, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: GoldRateRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(row),
    },
    ...(row.isActive
      ? []
      : [
          {
            id: 'activate',
            label: 'Set as Active',
            icon: <IconSystem.check size={18} />,
            onClick: () => handleActivate(row),
          },
        ]),
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => handleDelete(row),
      variant: 'danger' as const,
    },
  ], [handleView, handleEdit, handleActivate, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<GoldRateRecord>[] = useMemo(() => [
    {
      id: 'rateDate',
      header: 'Rate Date',
      accessorKey: 'rateDate',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
    {
      id: 'ratePerGram',
      header: 'Rate Per Gram (INR)',
      accessorKey: 'ratePerGram',
      size: 170,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>
      ),
    },
    {
      id: 'rateBasis',
      header: 'Basis',
      accessorKey: 'rateBasis',
      size: 130,
      cell: ({ getValue }: any) => {
        const isFull = getValue() === 'FULL';
        return (
          <span
            title={isFull
              ? 'Gross — full gold value per gram; eligibility deducts GL outstanding'
              : 'Net — add-on headroom per gram, applied directly (legacy)'}
            style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: 600,
              padding: '0.1rem 0.5rem', borderRadius: '0.625rem',
              background: isFull ? '#dbeafe' : '#e2e8f0',
              color: isFull ? '#1d4ed8' : '#475569',
            }}
          >
            {isFull ? 'Gross' : 'Net (legacy)'}
          </span>
        );
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 110,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      id: 'createdBy',
      header: 'Created By',
      accessorKey: 'createdBy',
      size: 140,
      cell: ({ row }: any) => row.original.createdByName || row.original.createdBy,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE OPTIONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View'
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  // ============================================================================
  // ACTION BUTTONS
  // ============================================================================

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Gold Rate',
      onClick: handleAddNew,
      tooltip: 'Add New Gold Rate',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Gold Rate Master"
        icon={<IconSystem.chart size={20} />}
        description="Manage gold rates per gram. Only one rate can be active at any time."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search gold rates...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {/* Error State */}
        {apiError && goldRates.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load gold rates</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && goldRates.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
          {viewMode === 'list' && (
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              onSelectionChange={setSelectedRows}
              emptyMessage="No gold rates found"
            />
          )}

          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ height: '100%', overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '1.5rem',
                }}>
                  {filteredData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No gold rates found</p>
                    </div>
                  ) : (
                    pagedData.map(record => (
                      <GoldRateCard
                        key={record.id}
                        record={record}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onActivate={handleActivate}
                      />
                    ))
                  )}
                </div>
                {filteredData.length > 0 && (
                  <div className="pagination-footer" style={{ position: 'sticky', bottom: 0 }}>
                    <div className="pagination-info">
                      Showing {pagedData.length} of {filteredData.length} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={gridPageSize}
                        onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {safeGridPage + 1} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Gold Rate Master</p>
            </div>
          )}
        </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Business Unit"
                options={[
                  { value: '', label: 'All Business Units' },
                ]}
                value={buFilter}
                onChange={(val: string | string[] | null) => setBuFilter((val as string) || '')}
                placeholder="Search business units..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Rate Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Rate Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Rate Per Gram (Min)</label>
              <input
                type="number"
                className="form-input"
                value={rateMin}
                onChange={(e) => setRateMin(e.target.value)}
                placeholder="Min rate"
                min={0}
                step="0.01"
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Rate Per Gram (Max)</label>
              <input
                type="number"
                className="form-input"
                value={rateMax}
                onChange={(e) => setRateMax(e.target.value)}
                placeholder="Max rate"
                min={0}
                step="0.01"
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Created By"
                options={[
                  { value: '', label: 'All Users' },
                  { value: 'Arun Kumar', label: 'Arun Kumar' },
                  { value: 'Priya Sharma', label: 'Priya Sharma' },
                  { value: 'Rajesh Verma', label: 'Rajesh Verma' },
                  { value: 'Sneha Patel', label: 'Sneha Patel' },
                ]}
                value={createdByFilter}
                onChange={(val: string | string[] | null) => setCreatedByFilter((val as string) || '')}
                placeholder="Search users..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setStatusFilter('');
                setBuFilter('');
                setDateFrom('');
                setDateTo('');
                setRateMin('');
                setRateMax('');
                setCreatedByFilter('');
              }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setFilterOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <GoldRateFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <GoldRateViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default GoldRateMasterScreen;
